import Vue from 'vue';
import Router from 'vue-router';
import VueSocketio from 'vue-socket.io';
import _ from 'lodash';
import { io } from 'socket.io-client';
import Segment from '@/Segment';
import store from '@/store';
import featureFlag from '@/api/featureFlag';
import LoginApi from '@/api/LoginApi';
import auth0 from '@/utils/auth0';
import { PerformanceMarks, performanceService } from '@/utils/PerformanceService';
import InsightsViewer from '@/pages/responsive-pages/non-authenticated/InsightsViewer.vue';
import { noAuthRoutes } from './no-auth';
import { partialAuthRoutes } from './partial-auth';
import { authenticatedRoutes } from './authenticated';
import { otsAuthenticatedRoutes } from './ots-authenticated';
import { prefixRouteNames } from './prefixRouteNames';
import config from '../config';
import CustomersApi from '../api/CustomersApi';
import ResponsivePage from '../pages/responsive-pages/ResponsivePage.vue';
import UrlRedirect from '../pages/responsive-pages/UrlRedirect.vue';

const AuthenticateToken = () => import(/* webpackChunkName: "authenticated-base" */ '../pages/responsive-pages/authenticated/AuthenticateToken.vue');
// eslint-disable-next-line max-len
const OtsAuthenticateToken = () => import(/* webpackChunkName: "authenticated-base" */ '../pages/responsive-pages/authenticated/OtsAuthenticateToken.vue');
const SingleFactorAuthenticateToken = () => import('../pages/responsive-pages/single-factor-authenticated/SingleFactorAuthenticateToken.vue');
const Playground = () => import('../pages/base-ui-playground/Playground.vue');
const MortgageRefinanceFlow = () => import('../pages/responsive-pages/non-authenticated/mortgage-refinance/MortgageRefinanceFlow.vue');
const MortgageRefinanceLanding = () => import('../pages/responsive-pages/non-authenticated/mortgage-refinance/MortgageRefinanceLanding.vue');
const MemberInviteExistingCustomer = () => import('../pages/responsive-pages/invite-authenticated/member-invite/pages/MemberInviteExistingCustomer');

const { isNavigationFailure, NavigationFailureType } = Router;
const REDIRECT_TO_BLACKLIST = ['/login', '/signup', '/signup/details'];

const originalPush = Router.prototype.push;
Router.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(err => {
    if (!isNavigationFailure(err, NavigationFailureType.duplicated) && !isNavigationFailure(err, NavigationFailureType.redirected)) {
      throw err;
    }
  });
};

const originalReplace = Router.prototype.replace;
Router.prototype.replace = function replace(location) {
  return originalReplace.call(this, location).catch(err => {
    if (!isNavigationFailure(err, NavigationFailureType.duplicated) && !isNavigationFailure(err, NavigationFailureType.redirected)) {
      throw err;
    }
  });
};
// Disable datadog from calling clearResourceTimings and clearing entries before we manually log them
performance.clearResourceTimings = () => {};
Vue.use(Router);

function initWebSockets() {
  if (!Vue.prototype.$socket) {
    Vue.use(new VueSocketio({
      debug: false,
      connection: io(config.get().elsaPublicUrl, {
        path: '/websockets',
        transports: ['websocket', 'polling'],
        reconnectionAttempts: 3,
        autoConnect: false,
      }),
      vuex: {
        store,
        mutationPrefix: 'socket_',
      },
    }));
  }
  if (!Vue.prototype.$socket.connected) {
    Vue.prototype.$socket.connect();
  }
}

async function redirectToLogin(to, next) {
  // const isUsingInAppBrowser = usingWhatsappBrowser();
  // if (isUsingInAppBrowser && !_isFacebookOrInstagramApp()) {
  //   const redirectPath = '/facebook-dead-end';
  //   Segment.trackUserGot('redirectingToWhatsappDeadEnd', { redirectPath });
  //   const query = { ...to.query, redirectTo };
  //   next({ path: redirectPath, query });
  // }
  const redirectTo = _extractRedirectTo(to.path);
  const isPartiallyLoggedIn = await LoginApi.isPartialLoggedIn();
  if (isPartiallyLoggedIn) {
    const member = await CustomersApi.getPartiallyAuthenticatedMember();
    performanceService.markEnd(PerformanceMarks.AUTHENTICATE_TOKEN);
    if (member.hasOidc) {
      if (await _shouldShowGoogleDeadEndPage(member)) {
        const redirectPath = '/facebook-dead-end';
        Segment.trackUserGot('redirectingToFacebookDeadEnd', { redirectPath });
        const query = { ...to.query, redirectTo };
        next({ path: redirectPath, query });
      } else if (to.query.error_description === 'Access expired.') {
        const redirectPath = '/2fa-Link-Expired';
        Segment.trackUserGot('redirectingToResetPasswordLinkExpired', { redirectPath });
        next({ path: redirectPath });
      } else {
        Segment.trackUserGot('2faRedirectToAuth0Login');
        const redirectToForAuth0 = auth0.getEncodedRedirectTo(to);
        externalLinkRedirect(`/api/partial-auth/login-with-auth0?redirectTo=${redirectToForAuth0}`);
      }
      return;
    }
    Segment.trackUserGot('redirectToAdd2FaFromMain');
    externalLinkRedirect('/api/partial-auth/login-with-auth0/new');
    return;
  }
  const redirectPath = '/login';
  const query = { redirectTo, ...to.query };
  performanceService.markEnd(PerformanceMarks.AUTHENTICATE_TOKEN);
  next({ path: redirectPath, query });
}

async function redirectToOtsLogin(to, next) {
  const isPartiallyLoggedIn = await LoginApi.isPartialLoggedIn();
  if (isPartiallyLoggedIn) {
    Segment.trackUserGot('Ots_2faRedirectToAuth0Login');
    const redirectToForAuth0 = auth0.getEncodedRedirectTo(to);
    externalLinkRedirect(`/api/partial-auth/login-with-auth0?redirectTo=${redirectToForAuth0}`);
    return;
  }

  const redirectTo = _extractRedirectTo(to.path);
  const redirectPath = '/ots-login';
  const query = { redirectTo, ...to.query };
  next({ path: redirectPath, query });
}

async function _shouldShowGoogleDeadEndPage(member) {
  const showFacebookDeadEndFeatureFlag = await featureFlag.getTreatmentPartialAuth('facebook-app-dead-end');
  return (showFacebookDeadEndFeatureFlag === 'on' && member.loginMethod
    && _.startsWith(member.loginMethod, 'google') && _isFacebookOrInstagramApp());
}

// async function usingWhatsappBrowser() {
//   const showFacebookDeadEndFeatureFlag = await featureFlag.getTreatmentPartialAuth('facebook-app-dead-end');
//   return (showFacebookDeadEndFeatureFlag === 'on' && _isInAppBrowser());
// }

function _isFacebookOrInstagramApp() {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return (userAgent.indexOf('FBAN') > -1) || (userAgent.indexOf('FBAV') > -1) || (userAgent.indexOf('Instagram') > -1);
}

// function _isInAppBrowser() {
//   const userAgent = navigator.userAgent || navigator.vendor || window.opera;
//   return (userAgent.indexOf('wv') > -1);
// }

function _extractRedirectTo(path) {
  if (_.includes(REDIRECT_TO_BLACKLIST, path)) {
    return 'sr';
  }
  return _.trimStart(path, '/');
}

const ELSA_V3_PREFIX = '/v3/';

function preserveWebContext(path) {
  // elsa-v3 is its own SPA with no /web tier, so its paths are never prefixed.
  if (path.startsWith(ELSA_V3_PREFIX)) {
    return path;
  }
  // Check if current location contains /web
  const isFromWebPath = window.location.pathname.includes('/web');

  // If coming from web path and destination doesn't already have /web, add it
  if (isFromWebPath && !path.includes('/web')) {
    return `/web${path.startsWith('/') ? path : `/${path}`}`;
  }

  return path;
}

function hardRefresh(path) {
  // eslint-disable-next-line no-restricted-syntax -- This is the hardRefresh implementation
  window.location.href = preserveWebContext(path);
}

function externalLinkRedirect(url) {
  // eslint-disable-next-line no-restricted-syntax -- This is the externalLinkRedirect implementation
  window.location.href = url;
}

// Function to generate the children routes configuration
function generateChildrenRoutes(prefix = '') {
  const addPrefix = name => (prefix ? `${prefix}_${name}` : name);

  return [
    ...prefixRouteNames(noAuthRoutes, prefix),
    ...prefixRouteNames([
      {
        path: '/ots',
        name: addPrefix('OtsAuthenticateToken'),
        component: OtsAuthenticateToken,
        async beforeEnter(to, from, next) {
          const isOtsLoggedIn = await LoginApi.isOtsIlLoggedIn();

          if (isOtsLoggedIn) {
            next();
            initWebSockets();
          } else {
            await redirectToOtsLogin(to, next);
          }
        },
        children: prefixRouteNames(otsAuthenticatedRoutes, prefix),
      }, {
        path: '/',
        name: addPrefix('SingleFactorAuthenticateToken'),
        component: SingleFactorAuthenticateToken,
        redirect: { name: addPrefix('StateRouter') },
        async beforeEnter(to, from, next) {
          if (await LoginApi.isPartialLoggedIn()) {
            next();
          } else {
            const query = { redirectTo: _extractRedirectTo(to.path), ...to.query };
            next({ path: 'login', query });
          }
        },
        children: prefixRouteNames(partialAuthRoutes, prefix),
      },
      {
        path: '/url-redirect',
        name: addPrefix('AuthenticateTokenRedirect'),
        component: UrlRedirect,
        async beforeEnter(to, from, next) {
          if (await LoginApi.isLoggedIn()) {
            next();
          } else {
            await redirectToLogin(to, next);
          }
        },
      },
      {
        path: '/invite-existing-customer',
        name: addPrefix('Member invite - existing customer'),
        component: MemberInviteExistingCustomer,
        async beforeEnter(to, from, next) {
          if (await LoginApi.isLoggedIn()) {
            next();
          } else {
            await redirectToLogin(to, next);
          }
        },
      },
      {
        path: '/',
        name: addPrefix('AuthenticateToken'),
        component: AuthenticateToken,
        redirect: { name: addPrefix('StateRouter') },
        async beforeEnter(to, from, next) {
          if (to.query.debug_performance) {
            sessionStorage.setItem('debug_performance', 'true');
          }
          performanceService.markStart(PerformanceMarks.AUTHENTICATE_TOKEN);
          if (await LoginApi.isLoggedIn()) {
            // Both dedupe in-flight calls: the cashflow guards await the flags (redesign redirect)
            // and AuthenticateToken awaits both, so starting them here keeps the two fetches
            // concurrent instead of serialising the session behind the flags.
            store.dispatch('featureFlags/init').catch(() => {});
            store.dispatch('session/initMinimal', { incognito: to.query.incognito }).catch(() => {});
            next();
            initWebSockets();
            performanceService.markEnd(PerformanceMarks.AUTHENTICATE_TOKEN);
          } else {
            await redirectToLogin(to, next);
          }
        },
        children: prefixRouteNames(authenticatedRoutes, prefix),
      },
    ], prefix),
  ];
}

const vueRouter = new Router({
  mode: 'history',
  routes: [
    {
      path: '/internal/playground',
      name: 'Playground',
      component: Playground,
    },
    {
      path: '/mortgage-refinance',
      name: 'MortgageRefinance',
      component: MortgageRefinanceFlow,
    },
    {
      // Facebook-ad funnel entry page (the ad links here). UI only — the
      // phone submit does not yet send the SMS. See MortgageRefinanceLanding.
      path: '/mortgage-refinance/start',
      name: 'MortgageRefinanceLanding',
      component: MortgageRefinanceLanding,
    },
    {
      path: '/internal/insights-viewer',
      name: 'InsightsViewer',
      component: InsightsViewer,
    },
    // Web path configuration (same children, different parent path)
    {
      path: '/web/',
      name: 'ResponsivePageWeb',
      component: ResponsivePage,
      children: generateChildrenRoutes('Web'),
    },
    {
      path: '/mob/',
      name: 'ResponsivePageMob',
      component: ResponsivePage,
      children: generateChildrenRoutes('Mob'),
    },
    {
      path: '/mobile/',
      name: 'ResponsivePageMobile',
      component: ResponsivePage,
      children: generateChildrenRoutes('Mobile'),
    },
    // Root path configuration
    {
      path: '/',
      name: 'ResponsivePage',
      component: ResponsivePage,
      children: generateChildrenRoutes(),
    },
  ],
});

// We want our "back" arrow in the app to go to previous place in app.
// However, if it's the first page, we don't want to go back to previous site or empty tab.
// Turns out that the router history does not expose the previous paths and if we want to check
// which page we came from we need to hook and record it ourselves.
// This puts the previous route name on the meta field of each route.
// The first page we came from will have a null value since the 'from.name' is empty.
vueRouter.beforeEach((to, from, next) => {
  _.extend(to.meta, { previousRoute: from });
  if (to.name !== from.name) {
    performanceService.clearMarks();
  }

  // Interceptor to handle /web path preservation during redirects
  if (from.path && from.path.includes('/web') && !to.path.includes('/web')) {
    // If coming from a /web path and going to a non-/web path, preserve the /web prefix
    const webPath = `/web${to.path}`;
    next({ path: webPath, query: to.query });
    return;
  }

  // Interceptor to handle /web route name preservation for named routes
  if (from.path && from.path.includes('/web') && to.name && !to.name.startsWith('Web_')) {
    // Try to find the corresponding Web_ route name
    const webRouteName = `Web_${to.name}`;
    // Check if this route exists in the router
    const found = vueRouter.resolve({ name: webRouteName, params: to.params, query: to.query });
    if (found && found.route && found.route.name === webRouteName) {
      next({ name: webRouteName, params: to.params, query: to.query });
      return;
    }
  }

  next();
});

vueRouter.canGoBack = route => {
  // The first page will have a 'null' value
  const isFirstNavigationPage = !route.meta?.previousRoute?.name;
  const canNavigateToPreviousComponent = !route.meta?.previousRoute?.meta?.preventRouterBack;
  return route.meta?.routerBack && !isFirstNavigationPage && canNavigateToPreviousComponent;
};

export default vueRouter;
export { hardRefresh, preserveWebContext, externalLinkRedirect };
